// Main game view component
import { useState, useRef, useCallback, useEffect } from 'react';
import { Story, Character, Scene, Choice, CHARACTER_CLASSES, CharacterStats, getStatModifier, formatModifier, getProficiencyBonus, getXpProgress, Enemy } from '../../types';
import { GameSettings } from '../Settings/Settings';
import { PromptLog } from '../PromptLog/PromptLog';
import { LevelUpNotification } from '../LevelUpNotification/LevelUpNotification';
import { CombatTracker } from './CombatTracker';
import './GameView.css';

type RollPhase = 'idle' | 'rolling' | 'result' | 'modified';

type StatAbbrev = 'STR' | 'DEX' | 'CON' | 'INT' | 'WIS' | 'CHA';

const STAT_KEYWORDS: Record<StatAbbrev, string[]> = {
  STR: ['push', 'pull', 'lift', 'break', 'force', 'smash', 'climb', 'grapple', 'strength', 'strong', 'shove', 'carry', 'bend', 'crush', 'punch', 'kick', 'strike', 'slam', 'barge', 'muscle', 'overpower', 'wrestle', 'pry'],
  DEX: ['dodge', 'sneak', 'hide', 'pick lock', 'acrobat', 'balance', 'reflex', 'stealth', 'dexterous', 'agile', 'nimble', 'tumble', 'evade', 'leap', 'jump', 'dash', 'sprint', 'roll', 'duck', 'sidestep', 'disarm', 'sleight', 'pickpocket', 'aim', 'shoot', 'throw'],
  CON: ['endure', 'resist', 'withstand', 'survive', 'stamina', 'tough', 'constitution', 'poison', 'disease', 'hold breath', 'fortitude', 'hardy'],
  INT: ['recall', 'study', 'decipher', 'investigate', 'analyze', 'knowledge', 'arcana', 'identify', 'intelligence', 'smart', 'research', 'examine', 'inspect', 'read', 'translate', 'decode', 'logic', 'solve', 'puzzle', 'spell', 'cast', 'magic', 'enchant', 'conjure'],
  WIS: ['perceive', 'sense', 'intuit', 'track', 'notice', 'insight', 'wisdom', 'meditat', 'pray', 'heal', 'medicine', 'nature', 'animal', 'detect', 'listen', 'observe', 'spot', 'forage', 'survive', 'calm'],
  CHA: ['persuade', 'deceive', 'intimidate', 'charm', 'convince', 'negotiate', 'diplomac', 'charisma', 'perform', 'bluff', 'lie', 'seduce', 'inspire', 'rally', 'barter', 'haggle', 'talk', 'speech', 'sing', 'distract', 'taunt'],
};

const STAT_KEY_MAP: Record<StatAbbrev, keyof CharacterStats> = {
  STR: 'str', DEX: 'dex', CON: 'con', INT: 'int', WIS: 'wis', CHA: 'cha',
};

function inferStatFromChoice(choiceText: string): StatAbbrev {
  const lower = choiceText.toLowerCase();
  let bestStat: StatAbbrev = 'STR';
  let bestScore = 0;

  for (const [stat, keywords] of Object.entries(STAT_KEYWORDS) as [StatAbbrev, string[]][]) {
    let score = 0;
    for (const kw of keywords) {
      if (lower.includes(kw)) score++;
    }
    if (score > bestScore) {
      bestScore = score;
      bestStat = stat;
    }
  }

  return bestStat;
}

function parseRollType(rollType: string | undefined): { modifier: number; stat: string } | null {
  if (!rollType || rollType === 'd20') return null;
  const match = rollType.match(/^d20([+-]\d+)\s+(\w+)$/);
  if (!match) return null;
  return { modifier: parseInt(match[1], 10), stat: match[2] };
}

function getResultClass(roll: number): string {
  if (roll === 20) return 'roll-nat20';
  if (roll === 1) return 'roll-nat1';
  if (roll >= 15) return 'roll-high';
  if (roll >= 6) return 'roll-mid';
  return 'roll-low';
}

function getResultLabel(roll: number): string {
  if (roll === 20) return 'Natural 20!';
  if (roll === 1) return 'Critical Fail!';
  if (roll >= 15) return 'Great Roll!';
  if (roll >= 10) return 'Decent Roll';
  if (roll >= 6) return 'Mediocre Roll';
  return 'Poor Roll';
}

interface GameViewProps {
  story: Story;
  character: Character;
  currentScene: Scene;
  choices: Choice[];
  sceneHistory: Scene[];
  settings?: GameSettings;
  getPromptLog?: (sessionId: string) => Promise<any[]>;
  getActiveCombat?: (sessionId: string) => Promise<any>;
  leveledUp?: boolean;
  previousLevel?: number;
  onLevelUpDismiss?: () => void;
  onMakeChoice: (index: number) => void;
  onRoll: (result: number, modifier: number, statAbbrev: string) => void;
  onNewGame: () => void;
  onCharacterUpdate?: (character: Character) => void;
  loading?: boolean;
}

export function GameView({
  story,
  character,
  currentScene,
  choices,
  sceneHistory,
  settings,
  getPromptLog,
  leveledUp,
  previousLevel,
  onLevelUpDismiss,
  onMakeChoice,
  onRoll,
  onNewGame,
  onCharacterUpdate,
  loading,
  getActiveCombat,
}: GameViewProps) {
  const [showStats, setShowStats] = useState(true);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [expandedScenes, setExpandedScenes] = useState<Set<string>>(new Set());
  const [debugModalOpen, setDebugModalOpen] = useState(false);
  const [rollModalOpen, setRollModalOpen] = useState(false);
  const [enemies, setEnemies] = useState<Enemy[]>([]);
  const [rollPhase, setRollPhase] = useState<RollPhase>('idle');
  const [displayNumber, setDisplayNumber] = useState(0);
  const [finalResult, setFinalResult] = useState(0);
  const [currentRollInfo, setCurrentRollInfo] = useState<{ statAbbrev: StatAbbrev; modifier: number } | null>(null);
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const characterClass = character.class || 'warrior';
  const classInfo = CHARACTER_CLASSES[characterClass];
  
  // Calculate stat modifiers based on character
  const statModifiers = {
    str: getStatModifier(character.stats.str),
    dex: getStatModifier(character.stats.dex),
    con: getStatModifier(character.stats.con),
    int: getStatModifier(character.stats.int),
    wis: getStatModifier(character.stats.wis),
    cha: getStatModifier(character.stats.cha),
  };
  const profBonus = getProficiencyBonus(character.level || 1);
  const xpProgress = getXpProgress(character.xp || 0);

  useEffect(() => {
    if (getActiveCombat && story.sessionId) {
      getActiveCombat(story.sessionId)
        .then((encounter: { enemies?: Enemy[] } | null) => {
          if (encounter && encounter.enemies) {
            setEnemies(encounter.enemies);
          } else {
            setEnemies([]);
          }
        })
        .catch(() => {
          setEnemies([]);
        });
    }
  }, [story.sessionId, getActiveCombat]);

  const clearTimeouts = useCallback(() => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
  }, []);

  useEffect(() => {
    return () => clearTimeouts();
  }, [clearTimeouts]);

  const toggleScene = (sceneId: string) => {
    setExpandedScenes(prev => {
      const next = new Set(prev);
      if (next.has(sceneId)) {
        next.delete(sceneId);
      } else {
        next.add(sceneId);
      }
      return next;
    });
  };

  const expandAllScenes = () => {
    setExpandedScenes(new Set(sceneHistory.map(s => s.id)));
  };

  const collapseAllScenes = () => {
    setExpandedScenes(new Set());
  };

  const handleChoiceClick = (index: number) => {
    const choice = choices[index];
    onMakeChoice(index);
    if (choice.needsRoll) {
      const stat = inferStatFromChoice(choice.description);
      const statValue = character.stats[STAT_KEY_MAP[stat]];
      const modifier = getStatModifier(statValue);
      setCurrentRollInfo({ statAbbrev: stat, modifier });
      setRollModalOpen(true);
      setRollPhase('idle');
    }
  };

  const [usingItemId, setUsingItemId] = useState<string | null>(null);
  const [itemError, setItemError] = useState<string | null>(null);

  const handleUseItem = async (itemId: string) => {
    if (usingItemId) return;
    setUsingItemId(itemId);
    setItemError(null);
    try {
      const response = await fetch(`/api/stories/${story.sessionId}/use-item`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ itemId }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.character && onCharacterUpdate) {
          onCharacterUpdate(data.character);
        }
      } else {
        const data = await response.json().catch(() => ({ error: 'Failed to use item' }));
        setItemError(data.error || 'Failed to use item');
        setTimeout(() => setItemError(null), 3000);
      }
    } catch (error) {
      console.error('Error using item:', error);
      setItemError('Network error');
      setTimeout(() => setItemError(null), 3000);
    } finally {
      setUsingItemId(null);
    }
  };

  const handleRollClick = () => {
    const result = Math.floor(Math.random() * 20) + 1;
    setFinalResult(result);
    setRollPhase('rolling');

    const totalTicks = 15;
    let elapsed = 0;

    for (let i = 0; i < totalTicks; i++) {
      const delay = 50 + (i * i * 0.5); // decelerate: 50ms → ~150ms
      elapsed += delay;

      const t = setTimeout(() => {
        if (i < totalTicks - 1) {
          setDisplayNumber(Math.floor(Math.random() * 20) + 1);
        } else {
          // Last tick: show real result
          setDisplayNumber(result);
          setRollPhase('result');

          // Show modifier after 800ms
          const modTimeout = setTimeout(() => {
            setRollPhase('modified');

            // Hold modified phase for 1200ms then submit
            const submitTimeout = setTimeout(() => {
              setRollModalOpen(false);
              setRollPhase('idle');
              const mod = currentRollInfo?.modifier ?? 0;
              const stat = currentRollInfo?.statAbbrev ?? 'STR';
              onRoll(result, mod, stat);
              setCurrentRollInfo(null);
            }, 1200);
            timeoutsRef.current.push(submitTimeout);
          }, 800);
          timeoutsRef.current.push(modTimeout);
        }
      }, elapsed);
      timeoutsRef.current.push(t);
    }
  };

  return (
    <div className="game-view">
      <div className="game-main">
        {/* Story Panel */}
        <div className="panel story-panel">
          <div className="panel-header">
            <span className="panel-title">{story.title || 'Adventure'}</span>
            <button 
              className="history-toggle"
              onClick={() => setHistoryModalOpen(true)}
            >
              History
            </button>
            {settings?.enableDebugMode && getPromptLog && (
              <button 
                className="debug-toggle"
                onClick={() => setDebugModalOpen(true)}
              >
                Debug
              </button>
            )}
            <button 
              className="stats-toggle"
              onClick={() => setShowStats(!showStats)}
            >
              {showStats ? '◀' : '▶'} Stats
            </button>
          </div>
          
          <div className="scene-content">
            {leveledUp && previousLevel && character && (
              <LevelUpNotification
                previousLevel={previousLevel}
                currentLevel={character.level}
                onDismiss={onLevelUpDismiss || (() => {})}
              />
            )}
            
            {currentScene.title && (
              <h3 className="scene-title">{currentScene.title}</h3>
            )}
            {currentScene.location && (
              <div className="scene-location">{currentScene.location}</div>
            )}
            
            {/* Combat tracker - shows enemies when in combat */}
            {enemies.length > 0 && (
              <CombatTracker enemies={enemies} />
            )}
            

            {/* Choice results display */}
            {currentScene.choiceMade && (
              <div className="choice-result">
                <span className="choice-label">You chose:</span>
                <span className="choice-text">"{currentScene.choiceMade}"</span>
                {currentScene.rollResult !== undefined && (() => {
                  const modInfo = parseRollType(currentScene.rollType);
                  return (
                    <span className={`roll-result ${
                      currentScene.rollResult === 20 ? 'critical-success' :
                      currentScene.rollResult === 1 ? 'critical-failure' : ''
                    }`}>
                      Roll: {currentScene.rollResult}
                      {modInfo && (
                        <span className="roll-modifier-inline"> ({formatModifier(modInfo.modifier)} {modInfo.stat})</span>
                      )}
                    </span>
                  );
                })()}
                {currentScene.damage > 0 && (
                  <span className="damage">
                    -{currentScene.damage} HP
                    {currentScene.damageReason && ` — ${currentScene.damageReason}`}
                  </span>
                )}
                {currentScene.heal > 0 && (
                  <span className="heal">
                    +{currentScene.heal} HP
                    {currentScene.damageReason && ` — ${currentScene.damageReason}`}
                  </span>
                )}
                {currentScene.loot && currentScene.loot !== 'none' && (
                  <span className="loot">Found: {currentScene.loot}</span>
                )}
              </div>
            )}

            <div className="scene-text">
              {currentScene.text.split('\n\n').map((paragraph, i) => (
                <p key={i}>{paragraph}</p>
              ))}
            </div>
          </div>
        </div>

        {/* Death Screen or Choice Panel */}
        {choices.length === 0 && character.hp <= 0 ? (
          <div className="panel death-panel">
            <div className="death-content">
              <div className="death-icon">&#9760;</div>
              <h2 className="death-title">You Have Fallen</h2>
              <p className="death-subtitle">{character.name} the {classInfo.name} has perished.</p>
              <button className="btn btn-primary death-btn" onClick={onNewGame}>
                New Adventure
              </button>
            </div>
          </div>
        ) : (
          <div className="panel choice-panel">
            <div className="panel-header">
              <span className="panel-title">Actions</span>
            </div>

            <div className="choices-list">
              {choices.map((choice, index) => (
                <button
                  key={choice.id}
                  className={`choice-btn ${choice.needsRoll ? 'needs-roll' : ''}`}
                  onClick={() => handleChoiceClick(index)}
                  disabled={loading}
                >
                  <span className="choice-number">[{index + 1}]</span>
                  <span className="choice-description">{choice.description}</span>
                  {choice.needsRoll && (
                    <span className="choice-roll-tag">ROLL</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Stats Sidebar */}
      {showStats && (
        <aside className="stats-panel panel fade-in">
          <div className="panel-header">
            <span className="panel-title">Character</span>
          </div>
          
          <div className="character-info">
            <span className="char-class-icon">{classInfo.icon}</span>
            <span className="char-name">{character.name}</span>
            <span className="char-class">{classInfo.name}</span>
            <span className="char-level">Level {character.level || 1}</span>
          </div>

          <div className="xp-bar">
            <div
              className="xp-fill"
              style={{ width: `${xpProgress.percentage}%` }}
            />
            <span className="xp-text">
              {(character.level || 1) >= 20 ? 'MAX LEVEL' : `${xpProgress.current} / ${xpProgress.needed} XP`}
            </span>
          </div>

          <div className="hp-bar">
            <div
              className={`hp-fill${character.hp <= 0 ? ' dead' : ''}`}
              style={{ width: `${(character.hp / character.maxHp) * 100}%` }}
            />
            <span className="hp-text">
              {character.hp <= 0 ? 'DEAD' : `${character.hp} / ${character.maxHp} HP`}
            </span>
          </div>

          <div className="stats-list">
            <div className="stat-row">
              <span>STR</span>
              <span>{character.stats.str} ({formatModifier(getStatModifier(character.stats.str))})</span>
            </div>
            <div className="stat-row">
              <span>DEX</span>
              <span>{character.stats.dex} ({formatModifier(getStatModifier(character.stats.dex))})</span>
            </div>
            <div className="stat-row">
              <span>CON</span>
              <span>{character.stats.con} ({formatModifier(getStatModifier(character.stats.con))})</span>
            </div>
            <div className="stat-row">
              <span>INT</span>
              <span>{character.stats.int} ({formatModifier(getStatModifier(character.stats.int))})</span>
            </div>
            <div className="stat-row">
              <span>WIS</span>
              <span>{character.stats.wis} ({formatModifier(getStatModifier(character.stats.wis))})</span>
            </div>
            <div className="stat-row">
              <span>CHA</span>
              <span>{character.stats.cha} ({formatModifier(getStatModifier(character.stats.cha))})</span>
            </div>
          </div>

          {character.inventory.length > 0 && (
            <div className="inventory">
              <span className="panel-title">Inventory</span>
              {itemError && <div className="item-error">{itemError}</div>}
              <ul>
                {character.inventory.map((item) => (
                  <li key={item.id} className="inventory-item">
                    <span className="item-name">
                      {item.name} {item.quantity > 1 && `(x${item.quantity})`}
                    </span>
                    {item.type === 'consumable' && item.healAmount && (
                      <button
                        className="use-item-btn"
                        onClick={() => handleUseItem(item.id)}
                        disabled={loading || !!usingItemId || character.hp >= character.maxHp}
                        title={character.hp >= character.maxHp ? "HP is full" : "Use this item"}
                      >
                        {usingItemId === item.id ? '...' : 'Use'}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      )}

      {/* Roll Modal */}
      {rollModalOpen && (
        <div
          className="modal-overlay fade-in"
          onClick={rollPhase === 'idle' ? () => setRollModalOpen(false) : undefined}
        >
          <div className="roll-modal" onClick={(e) => e.stopPropagation()}>
            {rollPhase === 'idle' && (
              <>
                <h3>Roll for {currentRollInfo ? ({
                  STR: 'Strength', DEX: 'Dexterity', CON: 'Constitution',
                  INT: 'Intelligence', WIS: 'Wisdom', CHA: 'Charisma',
                } as Record<string, string>)[currentRollInfo.statAbbrev] : 'this action'}</h3>
                <p className="roll-modal-subtitle">
                  Click to roll your d20
                  {currentRollInfo && (
                    <span className="roll-modifier-preview"> ({formatModifier(currentRollInfo.modifier)} {currentRollInfo.statAbbrev})</span>
                  )}
                </p>
                <button
                  className="roll-btn-large"
                  onClick={handleRollClick}
                  disabled={loading}
                >
                  D20
                </button>
                <button
                  className="btn roll-cancel"
                  onClick={() => { setRollModalOpen(false); setCurrentRollInfo(null); }}
                >
                  Cancel
                </button>
              </>
            )}
            {rollPhase === 'rolling' && (
              <div className="roll-display">
                <div className="roll-number rolling">{displayNumber || '?'}</div>
                <div className="roll-label">Rolling...</div>
              </div>
            )}
            {(rollPhase === 'result' || rollPhase === 'modified') && (
              <div className="roll-display">
                <div className={`roll-number result-reveal ${getResultClass(finalResult)}`}>
                  {finalResult}
                </div>
                {rollPhase === 'modified' && currentRollInfo && (
                  <div className="roll-modifier-line">
                    <span className="roll-modifier-stat">
                      {formatModifier(currentRollInfo.modifier)} {currentRollInfo.statAbbrev}
                    </span>
                    {' = '}
                    <span className="roll-modifier-total">
                      {finalResult + currentRollInfo.modifier}
                    </span>
                  </div>
                )}
                <div className={`roll-label ${getResultClass(finalResult)}`}>
                  {getResultLabel(finalResult)}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* History Modal */}
      {historyModalOpen && (
        <div
          className="modal-overlay fade-in"
          onClick={() => setHistoryModalOpen(false)}
        >
          <div className="history-modal" onClick={(e) => e.stopPropagation()}>
            <div className="history-modal-header">
              <h3>Story History</h3>
              <div className="history-header-actions">
                <button 
                  className="history-expand-all-btn"
                  onClick={expandAllScenes}
                >
                  Expand All
                </button>
                <button 
                  className="history-collapse-all-btn"
                  onClick={collapseAllScenes}
                >
                  Collapse All
                </button>
                <button 
                  className="history-close-btn"
                  onClick={() => setHistoryModalOpen(false)}
                >
                  ×
                </button>
              </div>
            </div>
            <div className="history-list">
              {sceneHistory.map((scene, index) => {
                const isExpanded = expandedScenes.has(scene.id);
                return (
                  <div 
                    key={scene.id} 
                    className={`history-entry ${isExpanded ? 'expanded' : ''}`}
                  >
                    <button 
                      className="history-expand-btn"
                      onClick={() => toggleScene(scene.id)}
                      aria-expanded={isExpanded}
                    >
                      <span className="history-expand-icon">{isExpanded ? '▼' : '▶'}</span>
                    </button>
                    <div className="history-entry-content">
                      <div className="history-entry-header">
                        <span className="history-scene-number">#{index + 1}</span>
                        <span className="history-scene-title">
                          {scene.title || `Scene ${index + 1}`}
                        </span>
                        {scene.location && (
                          <span className="history-scene-location">{scene.location}</span>
                        )}
                      </div>
                      {scene.choiceMade && (
                        <div className="history-choice">
                          <span className="history-choice-label">Chose:</span>
                          <span className="history-choice-text">"{scene.choiceMade}"</span>
                          {scene.rollResult !== undefined && (
                            <span className={`history-roll ${scene.rollResult === 20 ? 'crit-success' : scene.rollResult === 1 ? 'crit-fail' : ''}`}>
                              Roll: {scene.rollResult}
                            </span>
                          )}
                        </div>
                      )}
                      {(scene.damage > 0 || scene.heal > 0) && (
                        <div className="history-effects">
                          {scene.damage > 0 && (
                            <span className="history-damage">-{scene.damage} HP</span>
                          )}
                          {scene.heal > 0 && (
                            <span className="history-heal">+{scene.heal} HP</span>
                          )}
                        </div>
                      )}
                      {isExpanded ? (
                        <div className="history-text-full">
                          {scene.text.split('\n\n').map((paragraph, i) => (
                            <p key={i}>{paragraph}</p>
                          ))}
                        </div>
                      ) : (
                        <div className="history-text-preview">
                          {scene.text.slice(0, 150)}{scene.text.length > 150 ? '...' : ''}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Debug Modal */}
      {debugModalOpen && getPromptLog && (
        <PromptLog
          sessionId={story.sessionId}
          onClose={() => setDebugModalOpen(false)}
          getPromptLog={getPromptLog}
        />
      )}
    </div>
  );
}
